/**
 * GlobalView — Base Layer Service (§12.1)
 *
 * Configures the base imagery layer for the globe.
 * Supports Standard, Satellite, and Dark map styles.
 * Uses CesiumJS v1.124+ async imagery provider API.
 */

import * as Cesium from 'cesium';

export type BaseLayerStyle = 'standard' | 'satellite' | 'dark';

/**
 * Apply a base imagery layer style to the viewer.
 * Loads imagery asynchronously using the CesiumJS v1.124+ API.
 */
export async function setBaseLayerStyle(
  viewer: Cesium.Viewer,
  style: BaseLayerStyle,
): Promise<void> {
  const layers = viewer.imageryLayers;

  // Remove all existing imagery layers
  layers.removeAll();

  switch (style) {
    case 'satellite': {
      // Cesium Ion satellite imagery (asset 2)
      const provider = await Cesium.IonImageryProvider.fromAssetId(2);
      layers.addImageryProvider(provider);
      break;
    }
    case 'dark':
      // Minimal dark appearance — globe base color handles this
      // No imagery layer needed for a fully dark look
      break;
    case 'standard':
    default: {
      // Cesium Ion default (Bing Maps Aerial with Labels, asset 3)
      const provider = await Cesium.IonImageryProvider.fromAssetId(3);
      layers.addImageryProvider(provider);
      break;
    }
  }
}

/**
 * Configure default globe visual settings for the Command Center look.
 */
export function configureGlobeAppearance(viewer: Cesium.Viewer): void {
  const scene = viewer.scene;
  const globe = scene.globe;

  // Atmosphere and sky
  if (scene.skyAtmosphere) {
    scene.skyAtmosphere.show = true;
  }
  scene.fog.enabled = true;
  scene.fog.density = 0.0002;
  scene.fog.minimumBrightness = 0.03;

  // Globe appearance
  globe.enableLighting = true;
  globe.showGroundAtmosphere = true;

  // Base color when no imagery loaded
  globe.baseColor = Cesium.Color.fromCssColorString('#0a0c10');

  // Depth test for underground
  globe.depthTestAgainstTerrain = true;
}
